/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useEffect, useId, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { ChevronRight, ExternalLink, Users } from "lucide-react";
import {
  applyCoverage,
  coverageLabel,
  coverageRun,
  planCoverage,
  staffFamilies,
  type Coverage,
  type CoveragePlan,
  type CoverageRun,
  type StaffFamily,
} from "../api/careTeam";
import { newIdempotencyKey, type ApiError } from "../api/client";
import { Badge, type BadgeTone } from "./ui/Badge";
import { Button } from "./ui/Button";
import { EmptyState } from "./ui/EmptyState";
import { ErrorMessage } from "./ui/ErrorMessage";
import { InfiniteList } from "./ui/InfiniteList";
import { Link } from "./ui/Link";
import { Modal } from "./ui/Modal";
import { Select } from "./ui/Select";
import { Spinner } from "./ui/Spinner";
import { BusyLabel } from "./ui/BusyLabel";
import { GuardedButton } from "./ui/GuardedButton";
import { CoverageChoice } from "./CoverageChoice";
import { Facts } from "./DetailBits";
import { STAFF_FAMILY_SORTS, sortChoice } from "../lib/sorting";
import { usePolling } from "../lib/usePolling";

const RUN_PARAM = "coverageRun";
const RUN_POLL_MILLIS = 5_000;
const RUN_STATES: Record<CoverageRun["status"], { label: string; tone: BadgeTone }> = {
  PENDING: { label: "In progress", tone: "info" },
  OK: { label: "Done", tone: "success" },
  FAILED: { label: "Stopped", tone: "error" },
};

function childCount(count: number): string {
  return count === 1 ? "1 child" : `${count} children`;
}

function familyCount(count: number): string {
  return count === 1 ? "1 family" : `${count} families`;
}

/**
 * The families a care-team member is on, and for a coordinator the bulk "cover every active
 * family" run: a dry run to review, then one paced run whose id stays in the URL so a reload
 * keeps following it.
 */
export function StaffCoverage({
  staffId,
  name,
  coordinator,
}: {
  staffId: string;
  name: string;
  coordinator: boolean;
}) {
  const headingId = useId();
  const [searchParams, setSearchParams] = useSearchParams();
  const sort = sortChoice(STAFF_FAMILY_SORTS, searchParams.get("sort") ?? "");
  const runId = searchParams.get(RUN_PARAM);
  const [items, setItems] = useState<StaffFamily[] | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [covering, setCovering] = useState(false);
  const [runRead, setRunRead] = useState<{ eventId: string; run: CoverageRun } | null>(null);
  const runPending = runRead?.eventId === runId && runRead.run.status === "PENDING";

  const setParam = (key: string, value: string | null) =>
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value === null) {
          next.delete(key);
        } else {
          next.set(key, value);
        }
        return next;
      },
      { replace: true },
    );

  const requestSeq = useRef(0);
  const loadPage = (pageToLoad: number) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    void staffFamilies(staffId, pageToLoad, sort).then((response) => {
      if (seq !== requestSeq.current) {
        return;
      }
      setLoading(false);
      if (!response.ok) {
        setError(response.error.message);
        return;
      }
      setError(null);
      setHasMore(response.value.hasMore);
      setPage(pageToLoad);
      if (pageToLoad === 1) {
        setItems(response.value.items);
        return;
      }
      setItems((previous) => {
        const seen = new Set((previous ?? []).map((item) => item.familyId));
        return [
          ...(previous ?? []),
          ...response.value.items.filter((item) => !seen.has(item.familyId)),
        ];
      });
    });
  };
  const loadPageRef = useRef(loadPage);
  loadPageRef.current = loadPage;

  useEffect(() => {
    setItems(null);
    loadPageRef.current(1);
  }, [staffId, sort]);

  return (
    <section aria-labelledby={headingId} className="border border-border bg-bg-elevated shadow-card space-y-4 rounded-3xl p-5 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id={headingId} className="font-display text-lg text-text-primary">
          Coverage
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            className="w-48"
            value={sort.value}
            options={STAFF_FAMILY_SORTS}
            onChange={(value) => setParam("sort", value)}
            ariaLabel="Sort coverage"
          />
          {coordinator && (
            <GuardedButton
              locked={runPending}
              hint={`${name} is already being added to families.`}
              variant="secondary"
              onClick={() => setCovering(true)}
            >
              <Users size={14} />
              Add to all families in care
            </GuardedButton>
          )}
        </div>
      </div>

      {runId !== null && (
        <CoverageRunPanel
          key={`${staffId}:${runId}`}
          staffId={staffId}
          eventId={runId}
          onRead={(run) => {
            setRunRead({ eventId: runId, run });
            if (run.status !== "PENDING") {
              loadPageRef.current(1);
            }
          }}
          onDismiss={() => setParam(RUN_PARAM, null)}
        />
      )}

      {items === null && error !== null ? (
        <ErrorMessage message={error} className="mb-0" />
      ) : items === null ? (
        <div className="grid place-items-center py-8">
          <Spinner size="md" />
        </div>
      ) : (
        <InfiniteList
          isLoading={loading}
          hasMore={hasMore}
          onLoadMore={() => loadPageRef.current(page + 1)}
          isEmpty={items.length === 0}
          currentPage={page}
          error={error}
          onRetry={() => loadPageRef.current(page + 1)}
          emptyState={
            <EmptyState
              icon={<Users size={20} />}
              tone="quiet"
              title="No families yet"
              description={`Families show here once ${name} is on their care team.`}
            />
          }
        >
          <ul className="divide-y divide-border/60">
            {items.map((family) => (
              <li key={family.familyId} className="group flex items-center justify-between gap-3 py-2.5 text-sm">
                <Link
                  to={`/care/families/${family.familyId}`}
                  className="inline-flex items-center gap-1 font-medium text-text-primary hover:text-interactive"
                >
                  Family {family.familyId}
                  <ChevronRight
                    size={14}
                    className="transition-transform duration-200 group-hover:translate-x-1"
                  />
                </Link>
                <span className="text-xs text-text-tertiary">
                  {childCount(family.childUserIds.length)}
                </span>
              </li>
            ))}
          </ul>
        </InfiniteList>
      )}

      <CoverAllModal
        isOpen={covering}
        staffId={staffId}
        name={name}
        onClose={() => setCovering(false)}
        onQueued={(eventId) => {
          setCovering(false);
          setParam(RUN_PARAM, eventId);
        }}
      />
    </section>
  );
}

/** A network failure or a server error, as during an api rollout; the run carries on in cron. */
function isTransient(httpStatus: number): boolean {
  return httpStatus === 0 || httpStatus >= 500;
}

/**
 * Follows one run until it finishes or its read is refused; a server error keeps following it.
 * Keyed by run, so a different run starts a fresh panel.
 */
function CoverageRunPanel({
  staffId,
  eventId,
  onRead,
  onDismiss,
}: {
  staffId: string;
  eventId: string;
  onRead: (run: CoverageRun) => void;
  onDismiss: () => void;
}) {
  const [run, setRun] = useState<CoverageRun | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [following, setFollowing] = useState(true);

  usePolling(
    async (isCurrent) => {
      const result = await coverageRun(staffId, eventId);
      if (!isCurrent()) {
        return false;
      }
      const again = result.ok
        ? result.value.status === "PENDING"
        : isTransient(result.error.httpStatus);
      if (result.ok) {
        setError(null);
        setRun(result.value);
        onRead(result.value);
      } else {
        setError(result.error.message);
      }
      setFollowing(again);
      return again;
    },
    { delayMillis: RUN_POLL_MILLIS, immediate: true },
  );

  const state = run === null ? null : RUN_STATES[run.status];
  return (
    <div className="space-y-3 rounded-2xl border border-border bg-bg-elevated p-4">
      {error !== null && <ErrorMessage message={error} className="mb-0" />}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span role="status" className="inline-flex items-center gap-2 text-sm text-text-primary">
          {run !== null && state !== null ? (
            <>
              <Badge tone={state.tone}>{state.label}</Badge>
              {`${run.processed} of ${familyCount(run.total)} done`}
            </>
          ) : (
            following && <Spinner size="sm" />
          )}
        </span>
        {!following && (
          <Button variant="ghost" size="sm" onClick={onDismiss}>
            Dismiss
          </Button>
        )}
      </div>
      {run !== null && (
        <>
          <Facts
            facts={[
              ["Coverage", coverageLabel(run.coverage)],
              ["Added", run.queued],
              ["Already on the team", run.alreadyCovered],
              ["Skipped", run.refused.length],
            ]}
          />
          {run.refused.length > 0 && (
            <ul className="space-y-1.5 text-sm">
              {run.refused.map((refusal) => (
                <li key={refusal.familyId} className="flex flex-wrap gap-x-2">
                  <Link
                    to={`/care/families/${refusal.familyId}`}
                    className="font-medium text-interactive hover:underline"
                  >
                    Family {refusal.familyId}
                  </Link>
                  <span className="text-text-secondary">{refusal.reason}</span>
                </li>
              ))}
            </ul>
          )}
          {run.lastError !== null && <ErrorMessage message={run.lastError} className="mb-0" />}
        </>
      )}
    </div>
  );
}

function applyRefusal(error: ApiError, name: string): string {
  return error.httpStatus === 409
    ? `${name} is already being added to families. Try again when that finishes.`
    : error.message;
}

/** A dry run's plan and the one idempotency key every apply of it, retries included, sends. */
type ReviewedPlan = { plan: CoveragePlan; idempotencyKey: string };

function CoverAllModal({
  isOpen,
  staffId,
  name,
  onClose,
  onQueued,
}: {
  isOpen: boolean;
  staffId: string;
  name: string;
  onClose: () => void;
  onQueued: (eventId: string) => void;
}) {
  const [coverage, setCoverage] = useState<Coverage>("SUPPORT");
  const [reviewed, setReviewed] = useState<ReviewedPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const requestSeq = useRef(0);

  useEffect(() => {
    requestSeq.current += 1;
    if (isOpen) {
      setCoverage("SUPPORT");
      setReviewed(null);
      setError(null);
      setBusy(false);
    }
  }, [isOpen]);

  async function preview() {
    const seq = ++requestSeq.current;
    setBusy(true);
    setError(null);
    const result = await planCoverage(staffId, coverage);
    if (seq !== requestSeq.current) {
      return;
    }
    setBusy(false);
    if (result.ok) {
      setReviewed({ plan: result.value, idempotencyKey: newIdempotencyKey() });
    } else {
      setError(result.error.message);
    }
  }

  async function apply({ plan: chosen, idempotencyKey }: ReviewedPlan) {
    const seq = ++requestSeq.current;
    setBusy(true);
    setError(null);
    const result = await applyCoverage(
      staffId,
      chosen.coverage,
      chosen.families.map((family) => family.familyId),
      idempotencyKey,
    );
    if (result.ok) {
      onQueued(result.value.eventId);
      return;
    }
    if (seq !== requestSeq.current) {
      return;
    }
    setBusy(false);
    setError(applyRefusal(result.error, name));
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Add ${name} to all families in care`} size="md">
      <div className="space-y-4 px-6 py-5">
        {reviewed === null ? (
          <>
            <CoverageChoice value={coverage} onChange={setCoverage} />
            <p className="text-sm leading-relaxed text-text-secondary">
              {`${name} joins the care team of every family in care they are not on yet. You review the list before anything changes.`}
            </p>
          </>
        ) : (
          <>
            <p className="text-sm font-medium text-text-primary">
              {`${familyCount(reviewed.plan.familyCount)} · ${childCount(reviewed.plan.childCount)}`}
            </p>
            <p className="text-sm leading-relaxed text-text-secondary">
              {reviewed.plan.familyCount === 0
                ? `${name} is already on the care team of every family in care.`
                : `${name} will join these families as a ${coverageLabel(reviewed.plan.coverage).toLowerCase()} coordinator. Changes roll out gradually, and you can follow along on this page.`}
            </p>
            {reviewed.plan.families.length > 0 && (
              <ul className="max-h-64 divide-y divide-border/60 overflow-y-auto rounded-2xl border border-border px-4">
                {reviewed.plan.families.map((family) => (
                  <li
                    key={family.familyId}
                    className="flex items-center justify-between gap-3 py-2 text-sm"
                  >
                    <Link
                      to={`/care/families/${family.familyId}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 font-medium text-interactive hover:underline"
                    >
                      Family {family.familyId}
                      <ExternalLink size={12} aria-hidden="true" />
                      <span className="sr-only">, opens in a new tab</span>
                    </Link>
                    <span className="text-xs text-text-tertiary">
                      {childCount(family.childUserIds.length)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
        {error !== null && (
          <p role="alert" className="text-sm font-medium text-error">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          {reviewed === null ? (
            <>
              <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
                Cancel
              </Button>
              <Button size="sm" disabled={busy} onClick={() => void preview()}>
                <BusyLabel busy={busy} busyText="Finding families…">
                  Preview families
                </BusyLabel>
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={() => setReviewed(null)} disabled={busy}>
                Back
              </Button>
              <Button
                size="sm"
                disabled={busy || reviewed.plan.familyCount === 0}
                onClick={() => void apply(reviewed)}
              >
                <BusyLabel busy={busy} busyText="Starting…">
                  {`Add to ${familyCount(reviewed.plan.familyCount)}`}
                </BusyLabel>
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
